/**
 * The founder's test cases (D154; api/_bamiq/founderTools.js), on the #/iq
 * page and only for his account: each one is a question he rated, the
 * original answer, his feedback, and the bar to judge by. He edits the bar,
 * marks it reviewed, reruns it on BAM IQ as it is now, and sees the revised
 * answer beside the original. "Copy for evals" hands the case to the GitHub
 * eval runner, to test a change before it ships.
 *
 * Player names arrive already masked as [P1], [P2]...: a case never holds a
 * child's name.
 */
import React, { useEffect, useState } from "react";
import { GOLD } from "../constants/theme";
import { renderMarkdown } from "./markdown";
import { VERDICTS, CAPABILITIES, DIMENSIONS, CAUSES, SAFETY_CHECKS, ratingLabel } from "./feedback";
import { caseForEvals } from "./founder";

const ff = "'DM Sans', sans-serif";
const btn = (C, primary) => ({
  padding: "6px 11px", borderRadius: 8, fontFamily: ff, fontSize: 12.5, fontWeight: 800, cursor: "pointer",
  border: primary ? "none" : `1px solid ${C.border}`, background: primary ? GOLD : "transparent", color: primary ? "#111" : C.text,
});
const READ = { paragraph: { margin: "0 0 8px", lineHeight: 1.55 }, heading: { fontWeight: 700, margin: "10px 0 4px" }, list: { margin: "0 0 8px", paddingLeft: 18 }, item: { margin: "2px 0" }, card: { display: "block", padding: 8, border: "1px solid rgba(128,128,128,.3)", borderRadius: 8, margin: "4px 0 8px" } };
const tagText = (tag, key) => <sup key={key} style={{ fontSize: 10, fontWeight: 800, opacity: 0.7 }}>{String(tag).replace(/^S/, "")}</sup>;

/** One line of what produced an answer: model, runtime, effort, cost, time. */
export function metaLine(a) {
  if (!a) return "";
  const parts = [String(a.model || "?").replace(/^claude-/, ""), a.runtime ? String(a.runtime).split("+").pop() : null, a.effort ? `effort ${a.effort}` : null,
    a.cost_usd != null ? `$${Number(a.cost_usd).toFixed(3)}` : null, a.timing?.total_ms != null ? `${(a.timing.total_ms / 1000).toFixed(1)} s` : null];
  return parts.filter(Boolean).join(" · ");
}

/** The eval runner's form of a case, as JSON to paste. */
export const evalsJson = (c) => JSON.stringify(caseForEvals(c), null, 2);

const chipBtn = (C, on) => ({ padding: "3px 9px", borderRadius: 999, border: `1px solid ${on ? GOLD : C.border}`, background: on ? `${GOLD}22` : "transparent", color: C.text, fontFamily: ff, fontSize: 12, cursor: "pointer" });
const lab = (C) => ({ fontSize: 12, color: C.textMid });

/** Pick any of a list, as chips. */
function Chips({ label, list, value = [], onChange, C }) {
  return (
    <div role="group" aria-label={label} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
      <span style={lab(C)}>{label}</span>
      {list.map(x => <button key={x} type="button" aria-pressed={value.includes(x)} onClick={() => onChange(value.includes(x) ? value.filter(v => v !== x) : [...value, x])} style={chipBtn(C, value.includes(x))}>{x}</button>)}
    </div>
  );
}

/**
 * His judgement of one answer (2026-10-25): 1 to 5 on each of the five, and the safety checks, which pass or fail
 * and are never averaged into a score. A rerun's automatic checks show beside his.
 */
function Judge({ a, which, iq, c, C }) {
  const j = a?.judgement || { scores: {}, safety: {} };
  const save = (next) => iq.updateCase(c.id, { judge: which, scores: next.scores, safety: next.safety });
  const n = Object.keys(j.scores || {}).length;
  // Optional, and folded away (2026-10-25: "Don't require me to manually score all five dimensions for every answer").
  return (
    <details data-judge={which} style={{ display: "grid", gap: 4, fontSize: 12, color: C.textMid }}>
      <summary style={{ cursor: "pointer" }}>Score it (optional){n ? ` · ${n} scored` : ""}{Object.values(j.safety || {}).includes("fail") ? " · a safety check failed" : ""}</summary>
      {DIMENSIONS.map(d => (
        <div key={d} role="group" aria-label={d} style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ flex: 1, minWidth: 0 }}>{d}</span>
          {[1, 2, 3, 4, 5].map(n => <button key={n} type="button" aria-pressed={j.scores?.[d] === n} aria-label={`${d}: ${n}`} onClick={() => save({ ...j, scores: { ...j.scores, [d]: n } })}
            style={{ ...chipBtn(C, j.scores?.[d] === n), padding: "1px 7px" }}>{n}</button>)}
        </div>
      ))}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginTop: 4 }}>
        <span>Safety</span>
        {SAFETY_CHECKS.map(k => {
          const v = j.safety?.[k];
          return <button key={k} type="button" aria-label={`${k}: ${v || "not checked"}`} onClick={() => save({ ...j, safety: { ...j.safety, [k]: v === "pass" ? "fail" : "pass" } })}
            style={{ ...chipBtn(C, !!v), borderColor: v === "fail" ? "#E57373" : v === "pass" ? GOLD : C.border, color: v === "fail" ? "#E57373" : C.text }}>{k}: {v || "—"}</button>;
        })}
      </div>
    </details>
  );
}
/** A rerun's automatic checks, always in view. */
const Checks = ({ a, C }) => (a?.checks ? <div style={{ fontSize: 11.5, color: a.checks.privacy === "fail" || a.checks.attribution === "fail" ? "#E57373" : C.textMid }}>
  Automatic checks: privacy {a.checks.privacy} · attribution {a.checks.attribution} · saving {a.checks.saving}{a.version ? ` · code ${a.version}` : ""}</div> : null);

/** What the library search found for an answer, and which of it was cited. */
function Found({ a, C }) {
  const found = a?.retrieved || [];
  if (!found.length && !(a?.refs || []).length) return null;
  const cited = new Set((a?.refs || []).map(r => r.key).filter(Boolean));
  return (
    <details style={{ fontSize: 11.5, color: C.textMid }}>
      <summary style={{ cursor: "pointer" }}>Sources: found {found.length}, cited {cited.size}</summary>
      <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>{found.map((s, i) => <li key={i}>{s.tag ? `${s.tag} ` : ""}{s.title || s.key}{cited.has(s.key) ? " (cited)" : ""}</li>)}</ul>
    </details>
  );
}

function Answer({ title, a, C }) {
  return (
    <div style={{ minWidth: 0, display: "grid", gap: 6, alignContent: "start" }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", color: C.textMid }}>{title}</div>
      <div style={{ fontSize: 11.5, color: C.textMid }}>{metaLine(a)}</div>
      <Found a={a} C={C}/>
      <div style={{ fontSize: 13.5, color: C.text, padding: 12, borderRadius: 10, border: `1px solid ${C.border}`, background: C.bgHover, maxHeight: 520, overflowY: "auto" }}>
        {a?.body ? renderMarkdown(a.body, { renderTag: tagText, styles: READ }) : <span style={{ color: C.textMid }}>Not run yet.</span>}
      </div>
    </div>
  );
}

function CaseCard({ c, iq, C, isMobile }) {
  const [open, setOpen] = useState(false);
  const [expect, setExpect] = useState(c.expect || "");
  const [forbidden, setForbidden] = useState(c.forbidden || "");
  const [behavior, setBehavior] = useState(c.behavior || "");
  const [causeNote, setCauseNote] = useState(c.cause_note || "");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const run = (c.runs || [])[0] || null;
  const question = (c.turns || []).at(-1)?.coach || "";
  const act = async (label, fn) => {
    setBusy(label); setError(null);
    try { await fn(); } catch (e) { setError(e.message || "That didn't work."); } finally { setBusy(null); }
  };
  const dirty = expect !== (c.expect || "") || forbidden !== (c.forbidden || "") || behavior !== (c.behavior || "") || causeNote !== (c.cause_note || "");
  // Masked again on the server before it's copied out of the platform (2026-10-25).
  const copy = async () => {
    if (c.status !== "reviewed") { setError("Approve it first."); return; }
    try { const b = await iq.exportCases([c.id]); await navigator.clipboard.writeText(evalsJson(b.cases[0])); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { setError("Couldn't copy."); }
  };
  const field = { width: "100%", boxSizing: "border-box", padding: "7px 9px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.inputBg, color: C.text, fontFamily: ff, fontSize: 13, resize: "vertical" };
  return (
    <div data-case={c.id} style={{ border: `1px solid ${C.border}`, borderRadius: 12, background: C.bgCard }}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", border: "none", background: "transparent", color: C.text, fontFamily: ff, textAlign: "left", cursor: "pointer" }}>
        <span style={{ fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 999, border: `1px solid ${c.kind === "keep" ? "#5AB584" : C.border}`, color: c.kind === "keep" ? "#5AB584" : C.textMid }}>{c.kind === "keep" ? "Keep" : "Fix"}</span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{question || "(no question)"}</span>
        {(c.capabilities || []).length > 0 && <span style={{ fontSize: 11.5, color: C.textMid, whiteSpace: "nowrap" }}>{c.capabilities.join(" · ")}</span>}
        {run?.verdict && <span style={{ fontSize: 11.5, color: C.textMid }}>last rerun: {run.verdict}</span>}
        <span style={{ fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: c.status === "reviewed" ? `${GOLD}2A` : C.bgHover, color: C.text }}>{c.status === "reviewed" ? "Approved" : "To check"}</span>
      </button>
      {open && (
        <div style={{ padding: "0 14px 14px", display: "grid", gap: 12 }}>
          {c.status !== "reviewed" && c.drafted && <div style={{ fontSize: 12.5, color: C.text, padding: "8px 10px", borderRadius: 8, background: `${GOLD}14`, border: `1px solid ${GOLD}44` }}>
            {c.drafted === "model" ? "Filled in for you from the answer and your note." : "Filled in from what you tapped."} Check the tags and the success criteria, fix anything off, then Approve.</div>}
          {(c.turns || []).length > 1 && <div style={{ fontSize: 12, color: C.textMid }}>Turn {c.turns.length} of a conversation; the earlier turns are replayed as they were.</div>}
          {c.plan_context && <div style={{ fontSize: 12, color: C.textMid }}>The original had a player's plan attached. A rerun doesn't include it.</div>}
          {c.feedback && (
            <div style={{ fontSize: 13, color: C.text }}>
              <span style={{ color: C.textMid }}>Your feedback: </span>{ratingLabel(c.feedback.rating)}
              {c.feedback.reasons?.length ? ` · ${c.feedback.reasons.join(", ").toLowerCase()}` : ""}{c.feedback.note ? ` · "${c.feedback.note}"` : ""}
            </div>
          )}
          <div role="group" aria-label="Kind" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={lab(C)}>This case is</span>
            {[["fix", "to fix"], ["keep", "to keep (a good answer, checked for regressions)"]].map(([k, l]) => <button key={k} type="button" aria-pressed={(c.kind || "fix") === k} onClick={() => act("kind", () => iq.updateCase(c.id, { kind: k }))} style={chipBtn(C, (c.kind || "fix") === k)}>{l}</button>)}
          </div>
          <Chips label="Capabilities" list={CAPABILITIES} value={c.capabilities || []} onChange={(v) => act("caps", () => iq.updateCase(c.id, { capabilities: v }))} C={C}/>
          <label style={{ display: "grid", gap: 4, fontSize: 12, color: C.textMid }}>The behavior being tested (one line)
            <input value={behavior} onChange={(e) => setBehavior(e.target.value)} maxLength={300} placeholder="e.g. knows when a quick thought needs a short answer" style={field}/>
          </label>
          <label style={{ display: "grid", gap: 4, fontSize: 12, color: C.textMid }}>What a successful answer accomplishes (outcomes, not wording)
            <textarea value={expect} onChange={(e) => setExpect(e.target.value)} rows={3} maxLength={2000} placeholder="e.g. Provides an immediately usable 2v2 exercise and avoids dogmatic claims" style={field}/>
          </label>
          <label style={{ display: "grid", gap: 4, fontSize: 12, color: C.textMid }}>What it must not do (optional)
            <textarea value={forbidden} onChange={(e) => setForbidden(e.target.value)} rows={2} maxLength={1000} style={field}/>
          </label>
          {c.kind !== "keep" && <>
            <Chips label="Likely cause" list={CAUSES} value={c.cause || []} onChange={(v) => act("cause", () => iq.updateCase(c.id, { cause: v }))} C={C}/>
            <label style={{ display: "grid", gap: 4, fontSize: 12, color: C.textMid }}>Why (optional; Claude adds its read in the analysis)
              <input value={causeNote} onChange={(e) => setCauseNote(e.target.value)} maxLength={500} style={field}/>
            </label>
          </>}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            {dirty && <button type="button" disabled={!!busy} onClick={() => act("save", () => iq.updateCase(c.id, { expect, forbidden, behavior, cause_note: causeNote }))} style={btn(C, true)}>Save</button>}
            <button type="button" disabled={!!busy || dirty} onClick={() => act("review", () => iq.updateCase(c.id, { status: c.status === "reviewed" ? "draft" : "reviewed" }))} style={btn(C, false)}>
              {c.status === "reviewed" ? "Back to checking" : "Approve"}</button>
            <button type="button" disabled={!!busy || c.status !== "reviewed"} onClick={() => act("rerun", () => iq.rerunCase(c.id))}
              title={c.status !== "reviewed" ? "Approve it first" : "Ask it again on BAM IQ as it is now. Nothing is saved to your conversations."}
              style={{ ...btn(C, c.status === "reviewed"), opacity: c.status === "reviewed" ? 1 : 0.5 }}>{busy === "rerun" ? "Rerunning…" : "Rerun now"}</button>
            <button type="button" onClick={copy} style={btn(C, false)}>{copied ? "Copied" : "Copy for evals"}</button>
            <span style={{ flex: 1 }}/>
            <button type="button" disabled={!!busy} onClick={() => act("delete", () => iq.deleteCase(c.id))} style={{ ...btn(C, false), color: "#E57373" }}>Delete</button>
          </div>
          {busy === "rerun" && <div style={{ fontSize: 12, color: C.textMid }}>Asking BAM IQ again. This takes as long as a normal answer and counts toward your monthly usage.</div>}
          {error && <div role="alert" style={{ fontSize: 12.5, color: "#E57373" }}>{error}</div>}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
            <div style={{ display: "grid", gap: 8, alignContent: "start" }}><Answer title="Original (baseline)" a={c.original} C={C}/><Judge a={c.original} which="original" iq={iq} c={c} C={C}/></div>
            <div style={{ display: "grid", gap: 8, alignContent: "start" }}><Answer title={run ? `Rerun ${new Date(run.at).toLocaleString()}` : "Rerun"} a={run} C={C}/>{run && <><Checks a={run} C={C}/><Judge a={run} which={run.at} iq={iq} c={c} C={C}/></>}</div>
          </div>
          {run && (
            <div role="group" aria-label="Your verdict" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: C.textMid }}>
              <span>The rerun is</span>
              {VERDICTS.map(v => (
                <button key={v} type="button" aria-pressed={run.verdict === v} onClick={() => act("verdict", () => iq.updateCase(c.id, { run_at: run.at, verdict: v }))}
                  style={{ ...btn(C, run.verdict === v), padding: "4px 10px" }}>{v}</button>
              ))}
            </div>
          )}
          {(c.runs || []).length > 1 && (
            <div style={{ fontSize: 11.5, color: C.textMid }}>Earlier reruns: {(c.runs || []).slice(1).map(r => `${new Date(r.at).toLocaleDateString()} ${metaLine(r)}${r.verdict ? ` (${r.verdict})` : ""}`).join("; ")}</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function BamIQCases({ iq, C, isMobile }) {
  useEffect(() => { if (iq.cases == null) iq.loadCases(); }, [iq]);
  const [show, setShow] = useState("all");
  const [exported, setExported] = useState(null);
  if (!iq.founder) return null;
  const all = iq.cases || [];
  const cases = all.filter(c => show === "all" || (c.kind || "fix") === show);
  const reviewed = all.filter(c => c.status === "reviewed").length;
  const exportAll = async () => {
    try {
      const bundle = await iq.exportCases();
      await navigator.clipboard.writeText(JSON.stringify(bundle, null, 2));
      setExported(`Copied ${bundle.cases.length} approved cases, player names and contact details removed. Paste it to Claude for the analysis.`);
    } catch { setExported("Couldn't copy. Try again."); }
  };
  return (
    <div style={{ height: "100%", overflowY: "auto", padding: isMobile ? 14 : 24, fontFamily: ff, boxSizing: "border-box" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gap: 12 }}>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 30, color: C.text }}>Test cases</div>
        <div style={{ fontSize: 13, color: C.textMid, lineHeight: 1.5 }}>
          Only you see this. Rate an answer, tap what's wrong or good, add a line, and "Save and make a test case". The rest is filled in for you:
          check it and Approve. Good answers become keep cases, rerun after a change to catch anything that got worse. Scores are optional.
          Player names are replaced with [P1], [P2]... in every case.
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
          {[["all", `All ${all.length}`], ["fix", `To fix ${all.filter(c => (c.kind || "fix") === "fix").length}`], ["keep", `To keep ${all.filter(c => c.kind === "keep").length}`]].map(([k, l]) =>
            <button key={k} type="button" aria-pressed={show === k} onClick={() => setShow(k)} style={chipBtn(C, show === k)}>{l}</button>)}
          <span style={{ flex: 1 }}/>
          <button type="button" disabled={!reviewed} onClick={exportAll} style={{ ...btn(C, !!reviewed), opacity: reviewed ? 1 : 0.5 }} title="Every approved case in full, with player names and contact details removed, for the analysis">Export approved ({reviewed})</button>
        </div>
        {exported && <div style={{ fontSize: 12, color: C.textMid }}>{exported}</div>}
        {iq.cases == null && <div style={{ fontSize: 13, color: C.textMid }}>Loading…</div>}
        {iq.cases != null && !cases.length && <div style={{ fontSize: 13, color: C.textMid }}>None yet.</div>}
        {cases.map(c => <CaseCard key={c.id} c={c} iq={iq} C={C} isMobile={isMobile}/>)}
      </div>
    </div>
  );
}
